import { useCallback, useState, useEffect } from 'react';
import { MapLayerToggle } from '../../../api/mapping/types';

export function useToggleManager(availableToggles: MapLayerToggle[]) {
  // Track which toggles are currently active (on/off state)
  const [activeToggleIds, setActiveToggleIds] = useState<Set<string>>(() => {
    return new Set(
      availableToggles.filter((toggle) => toggle.default).map((toggle) => toggle.title),
    );
  });

  // Automatically deactivate toggles when they become unavailable
  useEffect(() => {
    setActiveToggleIds((prev) => {
      const unavailableActive = Array.from(prev).filter((title) => {
        const toggle = availableToggles.find((t) => t.title === title);
        return toggle && (toggle as any).available === false;
      });

      if (unavailableActive.length > 0) {
        const newSet = new Set(prev);
        unavailableActive.forEach((title) => newSet.delete(title));
        return newSet;
      }

      return prev;
    });
  }, [availableToggles]);

  // Handle toggle state changes with mutual exclusivity support
  const handleToggleChange = useCallback(
    (toggleTitle: string, isActive: boolean) => {
      setActiveToggleIds((prev) => {
        const newSet = new Set(prev);
        if (isActive) {
          newSet.add(toggleTitle);

          // Handle mutual exclusivity: turn off any toggles specified in the 'off' array
          const toggle = availableToggles.find((t) => t.title === toggleTitle);
          if (toggle?.layers.off) {
            // Find and disable toggles that control the "off" layers
            availableToggles.forEach((otherToggle) => {
              if (otherToggle.title !== toggleTitle && otherToggle.layers.on) {
                // Check if this toggle controls any of the layers we need to turn off
                const controlsOffLayer = otherToggle.layers.on.some((layerId) =>
                  toggle.layers.off?.includes(layerId),
                );
                if (controlsOffLayer) {
                  newSet.delete(otherToggle.title);
                }
              }
            });
          }
        } else {
          newSet.delete(toggleTitle);
        }
        return newSet;
      });
    },
    [availableToggles],
  );

  return {
    activeToggleIds,
    setActiveToggleIds,
    handleToggleChange,
  };
}
